import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const SALES_TEAM_FIELDS = {
  name: "",
  description: "",

  // Team members (regular sales team members who share commission)
  // Each member: { userID: string }
  // Note: isManager field removed - manager shares are now location-based on User
  members: [],

  // Team PO ID for ordering
  poid: null,
  pojob: 1, // Current job number for team PO ID

  // Team sales goals by year
  // { [year]: targetAmount }
  goals: {},

  // Target contacts for the team (shared by all members)
  // Array of contact IDs, max 5
  targetContacts: [],

  // Whether this team is active
  active: true,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(SALES_TEAM_FIELDS);

export default class SalesTeam extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, SALES_TEAM_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: SalesTeam.canBeViewedBy,
      create: SalesTeam.canBeCreatedBy,
      edit: SalesTeam.canBeEditedBy,
      delete: SalesTeam.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isSuper;
  }

  static canBeCreatedBy(user) {
    return user.isSuper;
  }

  static canBeEditedBy(user) {
    return user.isSuper;
  }

  static canBeDeletedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return SalesTeam.getPath(this.id);
  }

  static getPath(id) {
    if (id) {
      return `salesTeams/${id}`;
    }
    return "salesTeams";
  }

  static getStateID(path) {
    const match = path.match(/^salesTeams\/([^/]+)$/);
    if (match) {
      return match[1];
    }
    return null;
  }

  get stateID() {
    return this.id;
  }

  // Helpers

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get isActive() {
    return this.data.active;
  }

  get memberCount() {
    return this.data.members.length;
  }

  // Get all member user IDs
  get memberIDs() {
    return this.data.members.map((m) => m.userID);
  }

  // Get team goal for a specific year
  getGoal(year) {
    return this.data.goals[year] || 0;
  }

  // Get current year's goal
  get currentYearGoal() {
    return this.getGoal(new Date().getFullYear());
  }

  // Check if a user is a member of this team
  isMember(userID) {
    return this.data.members.some((m) => m.userID === userID);
  }

  // Get a member's config by userID
  getMember(userID) {
    return this.data.members.find((m) => m.userID === userID) || null;
  }

  // Get commission split for team members (equal split)
  getCommissionSplit() {
    const count = this.data.members.length;
    return count > 0 ? 100 / count : 0;
  }

  // Get member user objects from store
  get memberUsers() {
    return this.data.members
      .map((m) => store.state.main.users[m.userID])
      .filter(Boolean);
  }

  // Validation

  get rules() {
    return {
      name: [(v) => Boolean(v && v.trim()) || "required"],
      poid: [
        (v) => !v || /^\d{1,3}$/.test(v) || "poidInvalid",
        (v) => !v || Number(v) >= 0 || "poidInvalid",
        (v) => !v || Number(v) <= 999 || "poidInvalid",
      ],
      "members.*.userID": [(v) => Boolean(v) || "required"],
    };
  }

  get isValid() {
    // Must have a name
    if (!this.data.name || !this.data.name.trim()) {
      return false;
    }
    // Must have at least one member
    if (this.data.members.length === 0) {
      return false;
    }
    // All members must have a userID
    if (!this.data.members.every((m) => m.userID)) {
      return false;
    }
    return true;
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, SALES_TEAM_FIELDS)));
  }

  isEqual(team) {
    return Boolean(this.id === team.id && isEmpty(diff(this.data, team.data)));
  }

  // Firestore

  toFirestore() {
    return {
      ...this.data,
    };
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new SalesTeam(snapshot.id, data);
  }
}
